import { CheckCircleFilled, ClockCircleFilled, MessageFilled } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import caregiversNoAi from 'assets/caregivers-no-ai.png';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from 'components/ui/dialog';
import usePreferences from 'hooks/usePreferences';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Platform from 'utils/Platform';

export const BETTER_NOTIFICATION_REQUEST_TIMING_FLAG = 'better-notification-request-timing';

export type PushNotificationOptInTrigger = 'first_conversation' | 'first_tracking' | 'questionnaire_completed';

type PushNotificationOptInContextValue = {
  requestOptIn: (trigger: PushNotificationOptInTrigger) => void;
  /**
   * Handles tapping a notification suggester on native:
   * - If the OS has never been asked for push permissions, calls requestPermissions()
   *   directly so iOS registers this app as notification-capable.
   * - If the permission is already denied (or granted), opens the app's OS Settings page.
   */
  enableNotifications: () => void;
};

const PushNotificationOptInContext = createContext<PushNotificationOptInContextValue | undefined>(undefined);

export function PushNotificationOptInProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<PushNotificationOptInTrigger | null>(null);
  const processingRef = useRef(false);

  const {
    value: isFlowCompleted,
    set: markFlowCompleted,
    loading: loadingFlowCompleted,
  } = usePreferences(Keys.PUSH_OPT_IN_FLOW_COMPLETED);

  const requestOptIn = useCallback(
    (trigger: PushNotificationOptInTrigger) => {
      if (!Platform.isNative()) return;
      if (!Capacitor.isPluginAvailable('PushNotifications')) return;
      if (!posthog.isFeatureEnabled(BETTER_NOTIFICATION_REQUEST_TIMING_FLAG)) return;
      if (loadingFlowCompleted) return;
      if (isFlowCompleted === true) return;
      if (processingRef.current || open) return;

      processingRef.current = true;
      PushNotifications.checkPermissions()
        .then(status => {
          posthog.capture('$set', {
            $set: { gynger_push_notifications_permission: status.receive },
          });
          if (status.receive === 'granted') {
            void PushNotifications.register();
            markFlowCompleted('true');
            posthog.capture('notifications.permission_already_granted', { trigger });
            return;
          }
          if (status.receive === 'denied') {
            markFlowCompleted('true');
            posthog.capture('notifications.permission_already_denied', { trigger });
            return;
          }
          triggerRef.current = trigger;
          setOpen(true);
          posthog.capture('notifications.permission_prompt_shown', { trigger });
        })
        .finally(() => {
          processingRef.current = false;
        });
    },
    [isFlowCompleted, loadingFlowCompleted, markFlowCompleted, open],
  );

  const enableNotifications = useCallback(() => {
    if (!Platform.isNative() || !Capacitor.isPluginAvailable('PushNotifications')) {
      return;
    }
    PushNotifications.checkPermissions().then(status => {
      if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
        // Never been asked: call requestPermissions so iOS registers the app.
        PushNotifications.requestPermissions().then(result => {
          posthog.capture('$set', {
            $set: { gynger_push_notifications_permission: result.receive },
          });
          if (result.receive === 'granted') {
            markFlowCompleted('true');
            void PushNotifications.register();
          }
          posthog.capture('notifications.enable_notifications_clicked', {
            result: result.receive,
          });
        });
      } else {
        // Already granted or denied: open system settings.
        NativeSettings.open({
          optionAndroid: AndroidSettings.AppNotification,
          optionIOS: IOSSettings.App,
        });
        posthog.capture('notifications.enable_notifications_clicked', { result: 'open_settings' });
      }
    });
  }, [markFlowCompleted]);

  const closeModal = () => setOpen(false);

  const onLater = () => {
    const trigger = triggerRef.current;
    closeModal();
    markFlowCompleted('true');
    if (trigger) {
      posthog.capture('notifications.permission_prompt_dismissed', { trigger });
    }
    triggerRef.current = null;
  };

  const onActivate = async () => {
    const trigger = triggerRef.current;
    closeModal();
    if (!trigger) return;

    posthog.capture('notifications.permission_prompt_confirm_clicked', { trigger });
    const result = await PushNotifications.requestPermissions();
    posthog.capture('$set', {
      $set: { gynger_push_notifications_permission: result.receive },
    });
    markFlowCompleted('true');
    const granted = result.receive === 'granted';
    posthog.capture('notifications.permission_result', {
      trigger,
      result: granted ? 'granted' : 'denied',
    });
    if (granted) {
      await PushNotifications.register();
    }
    triggerRef.current = null;
  };

  return (
    <PushNotificationOptInContext.Provider value={{ requestOptIn, enableNotifications }}>
      {children}
      {Platform.isNative() && Capacitor.isPluginAvailable('PushNotifications') && (
        <Dialog
          open={open}
          onOpenChange={isOpen => {
            if (!isOpen) onLater();
          }}
        >
          <DialogContent className="w-full sm:max-w-sm">
            <div className="flex flex-col items-center gap-6 pt-3 pb-6 text-center">
              <div className="relative">
                <img src={caregiversNoAi} alt="" className="mx-auto block h-auto w-20 rounded-lg" />
                <Badge variant="inverse" className="absolute -top-2 right-4 min-w-5 px-1.5">
                  1
                </Badge>
              </div>

              <DialogTitle className="text-lg leading-tight font-bold">{t('pushNotificationsOptIn.title')}</DialogTitle>

              <div className="flex w-full max-w-sm flex-col items-start px-3 text-center">
                <p>{t('pushNotificationsOptIn.body')}</p>
                <div className="flex w-fit flex-col justify-center gap-2.5 text-center">
                  <div className="flex items-center gap-2.5">
                    <MessageFilled className="text-lg text-subtle-foreground" />
                    <span>{t('pushNotificationsOptIn.bullets.0')}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <ClockCircleFilled className="text-lg text-subtle-foreground" />
                    <span>{t('pushNotificationsOptIn.bullets.1')}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <CheckCircleFilled className="text-lg text-subtle-foreground" />
                    <span>{t('pushNotificationsOptIn.bullets.2')}</span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="flex-row justify-end gap-2">
              <Button variant="link" onClick={onLater}>
                {t('pushNotificationsOptIn.later')}
              </Button>
              <Button
                onClick={() => {
                  void onActivate();
                }}
              >
                {t('pushNotificationsOptIn.activate')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </PushNotificationOptInContext.Provider>
  );
}

export function usePushNotificationOptIn(): PushNotificationOptInContextValue {
  const ctx = useContext(PushNotificationOptInContext);
  if (!ctx) throw new Error('usePushNotificationOptIn must be used within PushNotificationOptInProvider');
  return ctx;
}
